import { Handle, Position } from 'reactflow'
import { useContext, useMemo } from 'react'
import { getIOStyle, getLineColor, getWarningLineColor } from './common'
import { getNodeColor, getNodeIcon } from '../icons'
import EdgeLabel from './EdgeLabel'
import PlusButton from './PlusButton'
import { ADD_NEXT_PREFIX_ID } from '../Settings'
import { ConditionNode, GraphRoot } from '@/api/dashboard'
import { ExitNode } from '../components/ExitNode/ExitNode'
import { IBlockProps } from '../types'
import { calculateWarning } from '../util'
import { BranchLeft } from '../components/BranchLeft/BranchLeft'
import { BranchRight } from '../components/BranchRight/BranchRight'
import { DescBlock } from '@/layouts/campaigns/blocks/DescBlock'
import { ShadowHoverBlock } from '@/layouts/campaigns/components/ShadowHoverBlock'
import { CampaignContext, ICampaignContext } from '@/expr/Context'

export const CONDITION_NODE_WIDTH = 332

function hasTrueNextNode(graph: GraphRoot, node: ConditionNode): boolean {
  if (!graph.nodes) {
    return false
  }
  return !!(node.true_node_id && graph.nodes[node.true_node_id])
}

function hasFalseNextNode(graph: GraphRoot, node: ConditionNode): boolean {
  if (!graph.nodes) {
    return false
  }
  return !!(node.false_node_id && graph.nodes[node.false_node_id])
}

export default function ConditionBlock(props: IBlockProps) {
  const nodeColor = getNodeColor(props.data.node.model_type)
  const context = useContext(CampaignContext) as ICampaignContext

  const [warningText, warningDescText] =
    useMemo(() => {
      return calculateWarning(props.data.node, props.data.graph)
    }, [props.data.graph]) || []

  return (
    <>
      <ShadowHoverBlock selected={props.selected} color={nodeColor} hasNext={true}>
        {getNodeIcon(props.data.node.model_type)}
        <DescBlock
          node={props.data.node}
          width={CONDITION_NODE_WIDTH + 'px'}
          selected={props.selected}
          visitsData={props.data.visitsData}
        />
      </ShadowHoverBlock>
      {!hasFalseNextNode(props.data.graph, props.data.node as ConditionNode) && (
        <div
          className="absolute"
          style={{
            left: '-149px',
            top: `calc(100% + 4px)`,
          }}
        >
          <BranchLeft
            animate={context.historyMode && props.data.node.result === 'False'}
            style={{ color: warningText ? getWarningLineColor() : getLineColor() }}
          />
          <EdgeLabel
            value={'FALSE'}
            style={{
              top: '82px',
              left: '66px',
            }}
          />
          <PlusButton
            visible={true}
            id={ADD_NEXT_PREFIX_ID + props.data.node.id}
            color={warningText ? getWarningLineColor() : getLineColor()}
            style={{
              position: 'absolute',
              top: '128px',
              left: '-5px',
            }}
            onClick={(node: HTMLElement) => props.data.addClick(node, false)}
          />
          <Handle
            type={'source'}
            id={'output-false'}
            position={Position.Bottom}
            isConnectable={true}
            style={{
              ...getIOStyle(),
              borderColor: warningText ? getWarningLineColor() : getLineColor(),
              left: '8px',
              bottom: '-8px',
              zIndex: 0,
            }}
          />
          <ExitNode
            uiHandle={false}
            warningText={warningText}
            warningDescText={warningDescText}
            style={{
              left: '-27px',
              position: 'absolute',
              bottom: '-42px',
            }}
          />
        </div>
      )}

      {!hasTrueNextNode(props.data.graph, props.data.node as ConditionNode) && (
        <div
          className="absolute"
          style={{
            right: '-149px',
            top: `calc(100% + 4px)`,
          }}
        >
          <BranchRight
            animate={context.historyMode && props.data.node.result === 'True'}
            style={{ color: warningText ? getWarningLineColor() : getLineColor() }}
          />
          <EdgeLabel
            value={'TRUE'}
            style={{
              top: '82px',
              left: '66px',
            }}
          />
          <PlusButton
            visible={true}
            color={warningText ? getWarningLineColor() : getLineColor()}
            id={ADD_NEXT_PREFIX_ID + props.data.node.id}
            style={{
              position: 'absolute',
              top: '128px',
              right: '-5px',
            }}
            onClick={(node: HTMLElement) => props.data.addClick(node, true)}
          />
          <Handle
            type={'source'}
            id={'output-true'}
            position={Position.Bottom}
            isConnectable={true}
            style={{
              ...getIOStyle(),
              borderColor: warningText ? getWarningLineColor() : getLineColor(),
              left: 'auto',
              right: '-8px',
              bottom: '-8px',
              zIndex: 0,
            }}
          />
          <ExitNode
            uiHandle={false}
            warningText={warningText}
            warningDescText={warningDescText}
            style={{
              right: '-27px',
              position: 'absolute',
              bottom: '-42px',
            }}
          />
        </div>
      )}
    </>
  )
}
